import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Wallet, Receipt, CreditCard, Activity, Banknote, Smartphone, HelpCircle } from 'lucide-react';
import { StatCard, DynamicRevenueChart } from './DashboardShared';
import { salesOf } from '../../../data/store';
import { billNumberOf } from '../../../utils/billNumber';

// Where the money came from.
//
// Built like the rings on the Super Admin dashboard, which is the house style
// for this: a ring per item with a dot marking where its arc ends, a soft disc
// in the middle carrying the summary, and a legend that names each colour.
//
// Every ring keeps its own full-circle track behind it, and that is what makes
// the shape honest here. A ring is read against its own track — "card is 46% of
// the way round" — not against the ring beside it, which would be false: circles
// of different radii have different circumferences, so the same share draws a
// longer arc on the outer one. The tracks are the reason the reader is never
// asked to make that comparison, and the amounts are printed in the legend for
// anyone who wants to.
//
// Two faults underneath the drawing mattered more than the drawing. It counted
// bills rather than rupees — ninety per cent of bills being cash says nothing
// about how much of the takings were — and anything not exactly "CARD" was
// counted as cash, so a bill whose method was never recorded showed as cash and
// was stated as fact. Methods come from the data now, so one added later takes
// its own ring without being taught.
const METHOD_STYLE = {
  CASH: { colour: '#f59e0b', Icon: Banknote },
  CARD: { colour: '#3b82f6', Icon: CreditCard },
  UPI: { colour: '#8b5cf6', Icon: Smartphone },
};
const UNRECORDED = 'Not recorded';
const FALLBACK = { colour: '#94a3b8', Icon: HelpCircle };

// The bills table colours its payment chips from the same source, so a method
// looks the same wherever it appears on the page.
const METHOD_COLOURS = Object.fromEntries(
  Object.entries(METHOD_STYLE).map(([name, style]) => [name, style.colour]),
);
const FALLBACK_COLOUR = FALLBACK.colour;

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const RING_SIZE = 176;
const RING_STROKE = 11;
const RING_GAP = 7;

const PaymentMethodsWidget = ({ sales }) => {
  const { rows, total, bills } = useMemo(() => {
    const byMethod = new Map();
    let sum = 0;

    sales.forEach((s) => {
      const raw = (s.method || s.paymentMethod || s.paymentType || '').trim();
      const name = raw ? raw.toUpperCase() : UNRECORDED;
      const amount = Number(s.total) || 0;

      const at = byMethod.get(name) || { name, amount: 0, count: 0 };
      at.amount += amount;
      at.count += 1;
      byMethod.set(name, at);
      sum += amount;
    });

    return {
      rows: [...byMethod.values()]
        .map((r) => ({
          ...r,
          share: sum > 0 ? r.amount / sum : 0,
          pct: sum > 0 ? Math.round((r.amount / sum) * 100) : 0,
        }))
        .sort((a, b) => b.amount - a.amount),
      total: sum,
      bills: sales.length,
    };
  }, [sales]);

  const centre = RING_SIZE / 2;

  return (
    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-col w-full h-full overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-50 px-5 py-3.5 shrink-0">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.1em] text-[#334155]">PAYMENT METHODS</h3>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 tabular-nums">{bills} bills</span>
      </div>

      {rows.length === 0 ? (
        <p className="flex flex-1 items-center justify-center text-[12px] font-bold text-slate-300">No sales yet.</p>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 p-5">
          <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
            <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
              className="-rotate-90" role="img"
              aria-label={`Payment methods by value, ${inr(total)} collected`}>
              {rows.map((r, i) => {
                const colour = (METHOD_STYLE[r.name] || FALLBACK).colour;
                const radius = centre - RING_STROKE / 2 - i * (RING_STROKE + RING_GAP);
                if (radius <= 0) return null;

                const circumference = 2 * Math.PI * radius;
                const angle = r.share * 2 * Math.PI;

                return (
                  <g key={r.name}>
                    {/* The track. Each ring is read against this, which is what
                        keeps the shape honest with rings of different sizes. */}
                    <circle cx={centre} cy={centre} r={radius} fill="none"
                      stroke={colour} strokeWidth={RING_STROKE} opacity="0.12" />

                    <circle cx={centre} cy={centre} r={radius} fill="none"
                      stroke={colour} strokeWidth={RING_STROKE} strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={circumference - r.share * circumference}
                      className="transition-all duration-1000 ease-out">
                      <title>{`${r.name} — ${inr(r.amount)} (${r.pct}%)`}</title>
                    </circle>

                    {/* The dot marking where the arc ends. */}
                    {r.share > 0 && (
                      <circle
                        cx={centre + radius * Math.cos(angle)}
                        cy={centre + radius * Math.sin(angle)}
                        r={RING_STROKE / 2 - 1.5}
                        fill={colour} stroke="#fff" strokeWidth="1.5"
                        className="transition-all duration-1000 ease-out"
                      />
                    )}
                  </g>
                );
              })}
            </svg>

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="flex h-[74px] w-[74px] flex-col items-center justify-center rounded-full bg-slate-100/60">
                <span className="text-[13px] font-bold leading-none text-[#0d0d0d] tabular-nums">{inr(total)}</span>
                <span className="mt-1 text-[8px] font-bold uppercase tracking-[0.16em] text-slate-500">collected</span>
              </div>
            </div>
          </div>

          <ul className="flex w-full flex-col gap-3">
            {rows.map((r) => {
              const { colour, Icon } = METHOD_STYLE[r.name] || FALLBACK;
              return (
                <li key={r.name} className="flex items-center gap-2.5 min-w-0">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${colour}14`, color: colour }} aria-hidden="true">
                    <Icon size={13} strokeWidth={2.3} />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[12px] font-bold text-[#0d0d0d]">{r.name}</span>
                    <span className="text-[10px] font-semibold text-slate-400 tabular-nums">
                      {r.count} bill{r.count === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end">
                    <span className="text-[12.5px] font-bold text-[#0d0d0d] tabular-nums">{inr(r.amount)}</span>
                    <span className="text-[10px] font-bold tabular-nums" style={{ color: colour }}>{r.pct}%</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

PaymentMethodsWidget.propTypes = {
  sales: PropTypes.array.isRequired,
};

// The latest bills.
//
// A table, because this card is two thirds of the row: columns give a wide panel
// something to line up against, and a plain list left it looking thin.
//
// The table it replaced had two columns that could not be right. "Order ID"
// printed sale.id.slice(0, 8), which on an id of the form bill-1789029786084
// gives "#BILL-178" — the word "bill" twice, cut short, and identical for every
// bill taken in the same era. And "Date & Time" ran toLocaleTimeString over a
// value that carries no time, so every row in the shop's history read 12:00 AM.
//
// Customer replaces the fake clock, because that is what a person recognises a
// sale by.
const RecentSalesWidget = ({ sales }) => {
  const recent = useMemo(
    () => [...sales]
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
      .slice(0, 8),
    [sales],
  );

  const th = 'pb-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100';

  return (
    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-col w-full h-full overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-50 px-5 py-3.5">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.1em] text-[#334155]">RECENT TRANSACTIONS</h3>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 tabular-nums">
          {sales.length} total
        </span>
      </div>

      <div className="flex-1 overflow-x-auto px-5 pb-4 pt-3">
        {recent.length === 0 ? (
          <p className="py-10 text-center text-[12px] font-bold text-slate-300">No sales yet.</p>
        ) : (
          <table className="w-full min-w-[420px] border-collapse text-left">
            <thead>
              <tr>
                <th className={th}>Bill</th>
                <th className={th}>Customer</th>
                <th className={th}>Date</th>
                <th className={`${th} text-center`}>Payment</th>
                <th className={`${th} text-right`}>Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {recent.map((sale) => {
                const method = (sale.method || sale.paymentMethod || sale.paymentType || '').trim();
                const colour = METHOD_COLOURS[method.toUpperCase()] || FALLBACK_COLOUR;
                return (
                  <tr key={sale.id} className="transition-colors hover:bg-slate-50/70">
                    <td className="py-2.5 pr-3 text-[12px] font-bold text-slate-400 tabular-nums">
                      {billNumberOf(sale)}
                    </td>
                    <td className="max-w-[160px] truncate py-2.5 pr-3 text-[12.5px] font-bold text-[#0d0d0d]"
                      title={sale.customer}>
                      {sale.customer || 'Walk-in'}
                    </td>
                    <td className="py-2.5 pr-3 text-[11.5px] font-semibold text-slate-500 tabular-nums whitespace-nowrap">
                      {String(sale.date || '').slice(0, 10)}
                    </td>
                    <td className="py-2.5 pr-3 text-center">
                      <span className="inline-block rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: colour, backgroundColor: `${colour}14` }}>
                        {method || 'Not set'}
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-[13px] font-bold text-[#0d0d0d] tabular-nums whitespace-nowrap">
                      {inr(sale.total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

RecentSalesWidget.propTypes = {
  sales: PropTypes.array.isRequired,
};

export const PosModule = ({ org }) => {
  const sales = salesOf(org.id);
  const metrics = useMemo(() => {
    const today = new Date().toDateString();
    
    // Today's stats
    const todaySales = sales.filter(s => new Date(s.date).toDateString() === today);
    const todayRevenue = todaySales.reduce((s, x) => s + x.total, 0);
    const todayBills = todaySales.length;

    // Lifetime stats
    const totalRevenue = sales.reduce((s, x) => s + x.total, 0);
    const avgBill = sales.length ? (totalRevenue / sales.length) : 0;

    return { todayRevenue, todayBills, totalRevenue, avgBill };
  }, [org.id, sales]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-3 mt-4">
        <Receipt size={16} className="text-[#0d0d0d]" />
        Billing & POS
      </div>
      
      <div className="dash-enter grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Wallet} label="Today's Sales" value={`₹${metrics.todayRevenue.toFixed(2)}`} iconColor="#334155" trend={0.6} />
        <StatCard icon={Receipt} label="Today's Bills" value={metrics.todayBills} suffix="closed" iconColor="#334155" trend={1.7} />
        <StatCard icon={Activity} label="Avg Bill Value" value={`₹${metrics.avgBill.toFixed(2)}`} iconColor="#334155" trend={6.4} />
        <StatCard icon={CreditCard} label="Lifetime Revenue" value={`₹${metrics.totalRevenue.toFixed(2)}`} iconColor="#334155" trend={3.6} />
      </div>

      <DynamicRevenueChart total={metrics.totalRevenue} sales={sales} />
      
      <div className="dash-enter grid grid-cols-1 lg:grid-cols-3 gap-6">
         <div className="lg:col-span-2">
            <RecentSalesWidget sales={sales} />
         </div>
         <div className="lg:col-span-1">
            <PaymentMethodsWidget sales={sales} />
         </div>
      </div>
    </div>
  );
};

PosModule.propTypes = {
  org: PropTypes.object.isRequired,
};
